import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiArrowRight, FiCheckCircle, FiSend, FiLoader } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { signInWithMagicLink, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithMagicLink(email);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#09090b]">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card w-full max-w-md p-8 border-white/5"
      >
        <AnimatePresence mode="wait">
          {!sent ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-white mb-2">
                  Welcome
                </h2>
                <p className="text-slate-400">
                  Enter your email to receive a secure login link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Email Address</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-3.5 text-slate-500" />
                    <input 
                      type="email" 
                      required 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-zinc-900/50 border border-white/10 rounded-2xl pl-12 pr-4 py-3 text-white focus:border-orange-500 outline-none transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full btn-primary py-3.5 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <SafeIcon icon={FiLoader} className="animate-spin text-xl" />
                  ) : (
                    <>
                      <SafeIcon icon={FiSend} /> Send Magic Link
                    </>
                  )}
                </button>
              </form>
              
              <div className="mt-6 text-center text-[10px] text-slate-600">
                Tip: If you're an admin, use your registered admin email.
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 text-green-500">
                <SafeIcon icon={FiCheckCircle} className="text-4xl" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Check your inbox!</h3>
              <p className="text-slate-400 mb-8">
                We've sent a magic link to <span className="text-white font-medium">{email}</span>.<br/>
                Click the link to sign in instantly.
              </p>
              <button 
                onClick={() => setSent(false)}
                className="text-sm text-orange-500 hover:text-orange-400 font-bold flex items-center justify-center gap-2 mx-auto"
              >
                <SafeIcon icon={FiArrowRight} className="rotate-180" /> Use a different email
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Login;